import type { WallpaperId } from "../config/wallpapers";

import type { AssortmentType } from "./assortment";
import type { Bookmarks } from "./bookmarks";
import type { Currency } from "./currency";
import type { BadLicenseReason } from "./license";
import type { TranslationKey } from "./localization";
import type { SmartfitAuth } from "./sizing";

export enum InitialSetupStep {
  Welcome = "welcome",
  Locale = "locale",
  Network = "network",
  InitialUpdate = "initialUpdate",
  PersonalCode = "personalCode",
  StartupTimes = "startupTimes",
  BrandActivation = "brandActivation",
  SecondUpdate = "secondUpdate",
  Done = "done",
  Finished = "finished"
}

export type ProductFinderSidebarPosition = "left" | "right";

export type ProductFinderColumnCount = 3 | 4;

export enum ProductFinderViewMode {
  GroupedByBrandAndCategory = "grouped_by_brand_and_category",
  GroupedByCategoryOnly = "grouped_by_category_only"
}

export enum ProductFinderOverviewInitialTab {
  Brands = "brands",
  Categories = "categories",
  Details = "details",
  Search = "search"
}

export interface ModelYearDisplay {
  term: "modelYear" | "releaseYear";
  concealed: boolean;
}

export interface InitialSetupSettings {
  firstFinished?: string;
  finishedSteps: InitialSetupStep[];
}

export interface ToolSystemSettings {
  isPrintingActive: boolean;
  isMailActive: boolean;
}

export interface ToolSetting {
  meta: {
    toolVisible?: boolean;
    pinRequired?: boolean;
    touchedSettings?: boolean;
    viewedSettings?: boolean;
    entryFile?: string | Partial<Record<TranslationKey, string>>;
    highlighted?: boolean;
  };
  // Reserved for injecting ToolSystemSettings
  system?: never;
}

export interface BikepriceCalculatorToolSetting extends ToolSetting {
  margin: number;
  regionalAdjustment: number;
  defaultBatteryCapacity: number;
  showPurchasingPrice: boolean;
  showMarketValue: boolean;
}

export interface FinancialCalculatorCreditPeriod {
  months: number;
  value: number;
  active: boolean;
}

export interface BikeFinancialCalculatorToolSetting extends ToolSetting {
  showInterestRate?: boolean;
  showDeposit?: boolean;
  showTotalInterestRateCosts?: boolean;
  creditPeriods?: FinancialCalculatorCreditPeriod[];
}

export interface FrameRange {
  min: number;
  max: number;
}

export interface FrameSize {
  range?: FrameRange;
  mtb: FrameRange;
  trekking: FrameRange;
  city: FrameRange;
  race: FrameRange;
}

export interface InseamFactor {
  mtb: number;
  trekking: number;
  city: number;
  race: number;
}

export interface BikeFrameSizeCalculatorSetting extends ToolSetting {
  showInseamSlider: boolean;
  sizeTolerance: number;
  showAdditionalCategories: boolean;
  additionalSizeToleranceForMtbSport: number;
  showSizesInInches: boolean;
  inseamFactor: InseamFactor;
  frameSizes: FrameSize[];
}

export interface EnraToolSetting extends ToolSetting {
  dealerId?: string;
}

export interface AlteosToolSetting extends ToolSetting {
  shopUrl: string;
}

export interface ZegPlusGarantieToolSetting extends ToolSetting {
  marketCode: string;
}

export interface BikmoToolSetting extends ToolSetting {
  ref?: string;
}

export interface BicoShopToolSetting extends ToolSetting {
  dealerId?: string;
  isDealerIdSet?: boolean;
}

export interface BicoAssessmentToolSetting extends ToolSetting {
  assessmentUrl: string;
}

export interface BodyScanningCrmToolSetting extends ToolSetting {
  crmLoginToken?: string;
}

export interface BodyScanningAppointmentPrepToolSetting extends ToolSetting {
  appointmentPrepLoginToken?: string;
}

export interface SqlabProfilerToolSetting extends ToolSetting {
  username?: string;
  password?: string;
}

export interface BrandWorldToolSetting {
  veloconnect?: { key: string; username: string }[];
}

export interface KtmBookATestRideToolSetting extends ToolSetting {
  dealerTestRideUuid?: string;
}

export interface HusqvarnaBookATestRideToolSetting extends ToolSetting {
  dealerTestRideUuid?: string;
}

export interface GasGasBookATestRideToolSetting extends ToolSetting {
  dealerTestRideUuid?: string;
}

export interface SmartfitToolSetting extends ToolSetting {
  smartfitApiAuth?: SmartfitAuth;
}

export interface LeasingCheckToolSetting extends ToolSetting {
  username: string;
  password: string;
  jobradClientId?: string;
  jobradClientSecret?: string;
  jobradScope?: string;
}

// Public themes are hardcoded in the settings UI dropdown.
// Private themes (e.g. "theme-kasten") are set externally and shown
// in the dropdown only when already active — no code change required
// to support new private themes.
export type UpwayTheme = `theme-${string}`;

export interface UpwayToolSetting extends ToolSetting {
  margin: number;
  theme: UpwayTheme;
}

export interface ToolSettings {
  abus: ToolSetting;
  allAboutCompanyBike: ToolSetting;
  bikepriceCalculator: BikepriceCalculatorToolSetting;
  bikeFinancialCalculator: BikeFinancialCalculatorToolSetting;
  enra: EnraToolSetting;
  alteos: AlteosToolSetting;
  bikmo: BikmoToolSetting;
  bodyScanningCrm: BodyScanningCrmToolSetting;
  bodyScanningAppointmentPrep: BodyScanningAppointmentPrepToolSetting;
  leasingCheck: LeasingCheckToolSetting;
  jobRad: ToolSetting;
  wuerthBikeLease: ToolSetting;
  bcRemoteShop: ToolSetting;
  paulLange: ToolSetting;
  bicoShop: BicoShopToolSetting;
  bikeFrameSizeCalculator: BikeFrameSizeCalculatorSetting;
  bicoAssessment: BicoAssessmentToolSetting;
  cycleasy: ToolSetting;
  sqlabProfiler: SqlabProfilerToolSetting;
  wertgarantie: ToolSetting;
  linexoLeasing: ToolSetting;
  leaseABike: ToolSetting;
  businessbike: ToolSetting;
  easyBikeLeasing: ToolSetting;
  firmenRadl: ToolSetting;
  bikeleasing: ToolSetting;
  leaseMyBike: ToolSetting;
  willdienstrad: ToolSetting;
  deutscheDienstrad: ToolSetting;
  euroRad: ToolSetting;
  kazenMaier: ToolSetting;
  meinDienstrad: ToolSetting;
  zegPlusGarantie: ZegPlusGarantieToolSetting;
  ktmConfigurator: ToolSetting;
  ktmBookATestride: KtmBookATestRideToolSetting;
  ktmParts: ToolSetting;
  ktmWear: ToolSetting;
  husqvarnaConfigurator: ToolSetting;
  husqvarnaBookATestride: HusqvarnaBookATestRideToolSetting;
  husqvarnaParts: ToolSetting;
  husqvarnaWear: ToolSetting;
  gasgasConfigurator: ToolSetting;
  gasgasBookATestride: GasGasBookATestRideToolSetting;
  gasgasParts: ToolSetting;
  gasgasWear: ToolSetting;
  schwalbePartsAdviser: ToolSetting;
  hephaErgoAdjust: ToolSetting;
  smartfit: SmartfitToolSetting;
  boschPartsAdviser: ToolSetting;
  upway: UpwayToolSetting;
}

export interface MailSettings {
  active: boolean;
  fromName?: string;
  fromMail?: string;
  message?: string;
  ccActive: boolean;
  ccTo?: string;
}

export interface MailToPrintSettings {
  email?: string;
  subject?: string;
}

export interface PrintingSettings {
  active: boolean;
  pinRequired: boolean;
  mailToPrint: MailToPrintSettings;
}

export interface UpdaterSettings {
  lastSuccessfulUpdate?: number;
}

export enum AvailabilityPrioritization {
  InStockElsewhere = "inStockElsewhere",
  Supplier = "supplier"
}

export interface AvailabilityPrioritizationOptions {
  availabilityPrioritization: AvailabilityPrioritization;
  downgradeSoonAvailableAtSupplierThresholdInWeeks?: number;
}

export interface CustomizationSettings {
  bikeCenterName?: string;
  wallpaper?: WallpaperId;
  showPrices: boolean;
  showPricesFeaturesOnly: boolean;
  skipBrandWorldAnimationEnabled: boolean;
  productFinderSidebarPosition: ProductFinderSidebarPosition;
  productFinderColumnCount: ProductFinderColumnCount;
  productFinderViewMode: ProductFinderViewMode;
  productFinderOverviewInitialTab: ProductFinderOverviewInitialTab;
  modelYearDisplay: ModelYearDisplay;
  availabilityPrioritizationOptions: AvailabilityPrioritizationOptions;
  sessionDurationInMs: number;
  sessionEndWarningDurationInMs: number;
  dealerWebsite?: string;
  noPinRequiredForDealerWebsite?: boolean;
}

export interface LicenseSettings {
  lastLicenseCheck: number;
  licenseErrorReason?: BadLicenseReason;
}

export interface VeloconnectEndpointSetting {
  modalDismissed?: boolean;
}

export interface VeloconnectSettings {
  [key: string]: VeloconnectEndpointSetting;
}

export enum AutomaticAssortmentFilter {
  OnlyIfAvailable = "onlyIfAvailable",
  NotUnknown = "notUnknown",
  AvailableOrOrdered = "availableOrOrdered"
}

export type AvailabilityFilterVisibility =
  "dealer_only" | "supplier_only" | "both_default_dealer" | "both_default_everywhere";

export interface ProductFinderAssortmentFilterSettings {
  showAllModelYears: boolean;
  selectAssortmentAutomatically: boolean;
  hideAutomaticAssortmentFilter: boolean;
  showOnlyAvailableVariants: boolean;
  /**
   * Controls how availability filters are displayed in the filter modal.
   *
   * - `dealer_only`: Only show WAWI filters (hides Veloconnect from customers)
   * - `supplier_only`: Only show Veloconnect filters (hides WAWI from customers)
   * - `both_default_dealer`: Show both sources, RadioGroup defaults to "Dealer"
   * - `both_default_everywhere`: Show both sources, RadioGroup defaults to "Everywhere"
   *
   * @default "both_default_everywhere"
   */
  availabilityFilterVisibility: AvailabilityFilterVisibility;
}

export interface AssortmentPriceSettings {
  showAssortmentPrices: boolean;
  showFallbackPrices: boolean;
  useRrpAsOriginalPrice: boolean;
  showOriginalPrices: boolean;
  showSaleBadge: null | "word" | "percentage";
}

export interface AssortmentSettings {
  type: AssortmentType | null;
  productFinderFilterSettings: ProductFinderAssortmentFilterSettings;
  priceSettings: AssortmentPriceSettings;
  /** Hides the availability filter teaser shown in the product finder while no assortment is active. */
  teaserDismissed: boolean;

  [AssortmentType.Automatic]?: {
    filter: AutomaticAssortmentFilter;
  };
}

export interface FeatureFlags {
  mailEnabled: boolean;
  customAssortmentEnabled: boolean;
  videoConferenceEnabled: boolean;
  wawiEnabled: boolean;
  mobileEnabled: boolean;
  alteosAdsEnabled: boolean;
  /** This feature currently only exists for the Orbea brand. */
  variantCustomizationButtonEnabled: boolean;
}

export interface FeatureMetaInfo {
  visible: boolean;
}

export interface FeaturesMetaInfo {
  mail: FeatureMetaInfo;
  customAssortment: FeatureMetaInfo;
  videoConference: FeatureMetaInfo;
  wawi: FeatureMetaInfo;
  mobile: FeatureMetaInfo;
}

export interface Mobile {
  registrationUrl: string | null;
  masterToken: string | null;
}

export interface Settings {
  personalCode?: string;
  mobile: Mobile;
  currency: Currency;
  bookmarks: Bookmarks;
  initialSetup: InitialSetupSettings;
  mail: MailSettings;
  printing: PrintingSettings;
  toolSettings: ToolSettings;
  updater: UpdaterSettings;
  customization: CustomizationSettings;
  license: LicenseSettings;
  veloconnect: VeloconnectSettings;
  assortment: AssortmentSettings;
  features: FeatureFlags;
  featuresMeta: FeaturesMetaInfo;
}
